import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import { reportError } from 'src/easyclaim.app.libs/reportError';

import { EasyClaimDocumentType } from '../easyclaim.api.client.typescript/src/index';
import {
	DetailContainer,
	DetailsContainer,
	ImageFileInputType,
	ExampleModalImage,
	SelectImage,
	StyledInfoIcon,
} from '../easyclaim.app.components/index';

import { ScreenComponent } from './ScreenComponent';
import {
	determineNewStateAfterLoadingForRegistrationDocument,
	determineNewStateAfterLoadingForRegistrationDocumentInside,
	getImageFileInputType,
	getRegistrationDocumentResources,
} from './VehicleInformationScreenHelper';

interface VehicleInformationState {}

export interface VehicleInformationProps extends AppState {
	canAddLaterModal: boolean;
}

export class VehicleInformation extends ScreenComponent<
	VehicleInformationProps,
	VehicleInformationState
> {
	constructor(props: VehicleInformationProps) {
		super(props);

		this.state = {};

		this.showFrontRegistrationDocumentExampleModal =
			this.showFrontRegistrationDocumentExampleModal.bind(this);
		this.showBackRegistrationDocumentExampleModal =
			this.showBackRegistrationDocumentExampleModal.bind(this);
	}

	async showFrontRegistrationDocumentExampleModal() {
		const customerShortName =
			this.props.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ||
			null;

		const frontSideOfRegistrationCertificate = getRegistrationDocumentResources(
			'Front',
			customerShortName
		);

		const src = process.env.PUBLIC_URL + frontSideOfRegistrationCertificate.exampleImageSrc;

		const images: ExampleModalImage[] = [
			{
				src: src,
			},
		];

		await this.props.showExampleModal(
			images,
			'registrationDocumentExampleModal.headline',
			'button.close',
			'front-registration'
		);
	}

	async showBackRegistrationDocumentExampleModal() {
		const customerShortName =
			this.props.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ||
			null;

		const backSideOfRegistrationCertificate = getRegistrationDocumentResources(
			'Back',
			customerShortName
		);

		const src = process.env.PUBLIC_URL + backSideOfRegistrationCertificate.exampleImageSrc;

		const images: ExampleModalImage[] = [
			{
				src: src,
			},
		];

		await this.props.showExampleModal(
			images,
			'registrationDocumentExampleModal.headline',
			'button.close',
			'back-registration'
		);
	}

	public render() {
		const { registrationDocument, registrationDocument2 } = this.props;

		const titleColor = this.props.easyClaimCase?._configuration?.additionalStyleFileName
			? undefined
			: this.props.theme.colors.primary;

		const subtitleColor = this.props.easyClaimCase?._configuration?.additionalStyleFileName
			? undefined
			: this.props.theme.colors.secondary;

		const customerShortName =
			this.props.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ||
			null;

		const frontSideOfRegistrationCertificate = getRegistrationDocumentResources(
			'Front',
			customerShortName
		);
		const backSideOfRegistrationCertificate = getRegistrationDocumentResources(
			'Back',
			customerShortName
		);

		const imageFileInputType: ImageFileInputType = getImageFileInputType(customerShortName);

		return (
			<div className="main-container">
				<h2
					style={{
						color: titleColor,
					}}
					className="vehicle-information-headline"
				>
					<FormattedMessage id="vehicleInformation.headline.first"></FormattedMessage>
				</h2>
				<DetailsContainer>
					<DetailContainer>
						<h3
							style={{
								color: subtitleColor,
							}}
							className="vehicle-information-subtitle"
						>
							<FormattedMessage id="vehicleInformation.headline.second"></FormattedMessage>
						</h3>
						<p className="vehicle-information-paragraph">
							<FormattedMessage id="vehicleInformation.paragraph.first"></FormattedMessage>{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showFrontRegistrationDocumentExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'VehicleInformation.asyncAction' });
									});
								}}
							/>
						</p>
						<SelectImage
							caseId={this.props.easyClaimCase!.caseId}
							imageFileInputType={imageFileInputType}
							componentId="registrationDocument"
							fileType={EasyClaimDocumentType.RegistrationDocument}
							currentImage={registrationDocument}
							title="vehicleInformation.headline.second"
							placeholder={frontSideOfRegistrationCertificate.placeholder}
							onChange={(image) => {
								const newState = determineNewStateAfterLoadingForRegistrationDocument(
									this.props,
									image
								);

								void this.props.setAppState(newState);
							}}
							easyClaimService={this.props.easyClaimService}
							onShowExample={() => {
								void this.showFrontRegistrationDocumentExampleModal().catch((error: unknown) => {
									reportError(error, {
										context: 'VehicleInformation.showFrontRegistrationDocumentExampleModal',
									});
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</DetailContainer>
					<DetailContainer>
						<h3
							style={{
								color: subtitleColor,
							}}
							className="vehicle-information-subtitle"
						>
							<FormattedMessage id="vehicleInformation.headline.fourth"></FormattedMessage>
						</h3>
						<p className="vehicle-information-paragraph">
							<FormattedMessage id="vehicleInformation.paragraph.fourth"></FormattedMessage>{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showBackRegistrationDocumentExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'VehicleInformation.asyncAction' });
									});
								}}
							/>
						</p>
						<SelectImage
							caseId={this.props.easyClaimCase!.caseId}
							imageFileInputType={imageFileInputType}
							componentId="registrationDocument2"
							fileType={EasyClaimDocumentType.RegistrationDocumentInside}
							currentImage={registrationDocument2}
							title="vehicleInformation.headline.fourth"
							placeholder={backSideOfRegistrationCertificate.placeholder}
							onChange={(image) => {
								const newState = determineNewStateAfterLoadingForRegistrationDocumentInside(
									this.props,
									image
								);

								void this.props.setAppState(newState);
							}}
							easyClaimService={this.props.easyClaimService}
							onShowExample={() => {
								void this.showBackRegistrationDocumentExampleModal().catch((error: unknown) => {
									reportError(error, {
										context: 'VehicleInformation.showBackRegistrationDocumentExampleModal',
									});
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</DetailContainer>
				</DetailsContainer>
			</div>
		);
	}
}
